import { useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

/**
 * Modal popup that displays the full Requirements for Licensees text
 * inline. Triggered from the Footer "Requirements for Licensees" link.
 *
 * - Esc key dismisses
 * - Backdrop click dismisses
 * - Body scroll locked while open
 * - Portal-rendered so it escapes any parent stacking context
 */
export default function RequirementsModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (typeof document === "undefined") return null;

  const node = (
    <AnimatePresence>
      {open && (
        <motion.div
          key="req-modal-overlay"
          className="fixed inset-0 z-[10000] flex items-center justify-center px-4 py-6 md:py-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          style={{ height: "100dvh" }}
          data-testid="requirements-modal"
        >
          {/* backdrop */}
          <button
            type="button"
            aria-label="Close requirements"
            data-testid="requirements-modal-backdrop"
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm cursor-default"
          />

          {/* dialog */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="req-modal-title"
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-[820px] max-h-full bg-white rounded-[20px] shadow-[0_30px_60px_-15px_rgba(0,0,0,0.45)] flex flex-col overflow-hidden"
          >
            {/* sticky header */}
            <div className="flex items-start justify-between gap-4 px-6 md:px-8 py-5 border-b border-black/8 bg-white">
              <div>
                <div
                  className="text-[10px] md:text-[11px] tracking-[0.32em] uppercase text-[#0041E6] font-bold mb-1.5"
                  style={{ fontFamily: '"Inter", system-ui, sans-serif' }}
                >
                  Legal · Licensee Requirements
                </div>
                <h2
                  id="req-modal-title"
                  className="text-black tracking-tight"
                  style={{
                    fontFamily: '"Inter", "Helvetica Neue", system-ui, sans-serif',
                    fontWeight: 800,
                    letterSpacing: "-0.015em",
                    fontSize: "clamp(20px, 2.6vw, 28px)",
                    lineHeight: 1.2,
                  }}
                >
                  Requirements for Licensees
                </h2>
              </div>
              <button
                type="button"
                aria-label="Close"
                data-testid="requirements-modal-close"
                onClick={onClose}
                className="p-2 rounded-full hover:bg-black/5 active:scale-95 transition flex-shrink-0"
              >
                <X size={20} strokeWidth={2.4} />
              </button>
            </div>

            {/* scrollable body */}
            <div
              className="overflow-y-auto px-6 md:px-8 py-6 md:py-8 legal-content"
              style={{ fontFamily: '"Inter", system-ui, sans-serif' }}
            >
              <p>
                <strong>Requirements for Consumer Products Licensees Sellers</strong>
              </p>
              <p>
                There are a number of requirements that must be met to become and remain a
                consumer products licensee seller on eGeez.
              </p>

              <p>
                <strong>Must comply with our CSSPI requirements:</strong>
              </p>

              <h3>Credit Evaluation</h3>
              <p>
                Official licensees are subject to licensing verification and may be required to
                provide an official licensing agreement or letter of licensing confirmation to
                ensure there are no counterfeits or fake products protecting the IP of the
                licensors. eGeez has zero tolerance to counterfeit.
              </p>

              <h3>Safety</h3>
              <ul>
                <li>
                  eGeez Labor Standards aims to promote a safe, inclusive, and courteous workplace
                  wherever and whenever authorised products are offered through eGeez. Licensees
                  must uphold these standards and are expected to possess the knowledge,
                  capability, and control over their supply chain and stocks required to meet all
                  of eGeez&rsquo;s demands.
                </li>
                <li>
                  A major area of emphasis for eGeez&rsquo;s overall corporate responsibility
                  initiatives is ethical and sustainable sourcing. In order to achieve our goals,
                  we are dedicated to upholding human rights, minimising the environmental impact
                  of the supply chain, ensuring the integrity and safety of products, and
                  cooperating with licensors and others.
                </li>
              </ul>
              <p>
                Licensees who produce fresh produce products or food and beverages are subject to
                additional requirements by eGeez Happiness Onboarding Managers such as food safety
                certificates, non-gmo certifications and other related documents, if necessary.
              </p>

              <h3>Shipping</h3>
              <ul>
                <li>
                  An official licensee must already have partnerships with carrier(s) to ship
                  locally or internationally. (FedEx, DHL, UPS etc.)
                </li>
                <li>
                  Return labels must be provided to customers upon shipping (highly recommended)
                  through the eLicensee web portal to customers. If not, return labels can also be
                  uploaded by licensee when a return request is received by a customer on the web
                  portal. Always be prepared!
                </li>
                <li>
                  An official licensee may also have a third-party drop shipping company or
                  internal drop shipping facility to ship within the required licensed territory.
                </li>
                <li>
                  Safe packaging supply is the sole responsibility of the licensee. eGeez does
                  supply additional complimentary branded eGeez packaging supplies as perks (eGeez
                  tapes, pouches, tags, boxes) for a limited time.
                </li>
                <li>
                  A working computer or laptop by Microsoft or Apple is recommended to process
                  orders, manage products, change status, and navigate the eLicensee web portal.
                </li>
                <li>
                  Licensees must have sufficient IT resources and systems capabilities to interact
                  with and support the use of eGeez business.
                </li>
              </ul>

              <h3>Product Integrity</h3>
              <ul>
                <li>
                  Licensees must be knowledgeable of the product safety standards applicable to
                  their licensed consumer products in all countries of distribution.
                </li>
                <li>
                  Licensees must be able to manage corrective action plans, including product
                  recalls — i.e. inform eGeez to list products as inactive and stop the sale.
                </li>
                <li>
                  Licensees who produce food, health, and beauty products are subject to
                  additional requirements.
                </li>
                <li>
                  Licensee may not list their licensed products on eGeez if the items have not
                  been approved by the licensor. It is the licensee&rsquo;s sole responsibility to
                  ensure full compliance with the licensor as eGeez third-party marketplace is
                  exempted from any responsibility or liability.
                </li>
              </ul>

              <h3>Insurance</h3>
              <ul>
                <li>
                  Licensees must carry insurance with insurers acceptable to eGeez, and naming
                  eGeez as additional insureds.
                </li>
                <li>
                  Required insurance policies and minimum coverage limits will depend on the
                  licensed product categories listed with eGeez.
                </li>
              </ul>

              <p>
                <strong>Unsolicited Idea Submissions:</strong> It is eGeez policy not to accept
                unsolicited idea submissions. Regarding our policy, please go to eGeez{" "}
                <strong>&ldquo;Terms of Use&rdquo;</strong>.
              </p>
            </div>

            {/* sticky footer */}
            <div className="px-6 md:px-8 py-4 border-t border-black/8 bg-[#FAFAFA] flex items-center justify-end">
              <button
                type="button"
                onClick={onClose}
                data-testid="requirements-modal-close-bottom"
                className="inline-flex items-center bg-black hover:bg-[#0041E6] text-white text-[14px] font-semibold rounded-full px-6 h-[44px] transition"
              >
                Close
              </button>
            </div>
          </motion.div>

          {/* scoped typography for the modal body */}
          <style>{`
            [data-testid="requirements-modal"] .legal-content h3 { font-size: 16px; font-weight: 700; margin-top: 1.5rem; margin-bottom: 0.5rem; color: #0B0B0F; }
            [data-testid="requirements-modal"] .legal-content p { font-size: 15px; line-height: 1.7; color: rgba(0,0,0,0.78); margin-bottom: 0.85rem; }
            [data-testid="requirements-modal"] .legal-content ul { padding-left: 1.4rem; margin-bottom: 1rem; }
            [data-testid="requirements-modal"] .legal-content li { font-size: 15px; line-height: 1.7; color: rgba(0,0,0,0.78); margin-bottom: 0.4rem; }
            [data-testid="requirements-modal"] .legal-content strong { color: #0B0B0F; font-weight: 700; }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(node, document.body);
}
